import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getCookieConsent, type CookiePreferences } from "@/components/CookieConsent";
import { loadGa4 } from "@/lib/analytics/ga4";
import { loadPostHog } from "@/lib/analytics/posthog";
import { loadMouseflow, trackMouseflowPageView } from "@/lib/analytics/mouseflow";
import {
  setAnalyticsConsent,
  hasAnalyticsConsent,
  trackPageView,
  identifyUser,
  resetIdentity,
} from "@/lib/analytics";

/**
 * Global analytics bootstrap.
 *  - Mounted once inside <BrowserRouter>.
 *  - Loads GA4 + PostHog only after the user grants analytics cookie consent.
 *  - Fires SPA page_view on every route change (including /:locale changes).
 *  - Identifies / resets the user on auth changes.
 */
export function AnalyticsProvider() {
  const location = useLocation();
  const { user } = useAuth();
  const initialised = useRef(false);

  // Boot analytics on consent
  useEffect(() => {
    const apply = (prefs: CookiePreferences | null) => {
      const granted = !!prefs?.analytics;
      setAnalyticsConsent(granted);
      if (granted && !initialised.current) {
        initialised.current = true;
        void loadGa4().then(() => {
          // Fire the initial page view as soon as GA4 is ready.
          const path = location.pathname + location.search;
          const locale = path.split("/")[1];
          trackPageView(path, document.title, locale);
        });
        void loadPostHog();
        loadMouseflow();
      }
    };

    apply(getCookieConsent());
    const handler = (e: Event) => apply((e as CustomEvent<CookiePreferences>).detail ?? null);
    window.addEventListener("cookie-consent-change", handler);
    return () => window.removeEventListener("cookie-consent-change", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SPA route changes
  useEffect(() => {
    if (!hasAnalyticsConsent() || !initialised.current) return;
    const path = location.pathname + location.search;
    const locale = location.pathname.split("/")[1];
    // Defer to next tick so document.title has updated.
    const id = window.setTimeout(() => {
      trackPageView(path, document.title, locale);
      trackMouseflowPageView(path);
    }, 0);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.search]);

  // Identify on auth change
  useEffect(() => {
    if (!hasAnalyticsConsent()) return;
    if (user?.id) {
      identifyUser(user.id, { email: user.email ?? undefined });
    } else {
      resetIdentity();
    }
  }, [user?.id]);

  return null;
}
